import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { company, stats } from '../../data/mockData';
import CountUp from '../CountUp';

const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30">
        <div
          className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        />
        <div
          className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-500/10 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${scrollY * 0.2}px)`, animationDelay: '1s' }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 sm:pt-32 sm:pb-20 md:py-32 text-center">
        <h1
          className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-5 md:mb-6 animate-fade-in-up"
          style={{ animationDelay: '0.1s', lineHeight: '1.15' }}
        >
          India's Leading
          <span className="block mt-1 text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-size-200 animate-gradient">
            Industrial Safety and Material
          </span>
          <span className="block -mt-1 md:-mt-2 text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-size-200 animate-gradient">
            Handling Solutions Provider
          </span>
        </h1>

        <p
          className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto mb-8 md:mb-12 leading-relaxed animate-fade-in-up"
          style={{ animationDelay: '0.2s' }}
        >
          {company.description}
        </p>

        <div
          className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mb-12 md:mb-20 animate-fade-in-up"
          style={{ animationDelay: '0.3s' }}
        >
          <Link to="/products" className="group relative rounded-xl font-semibold transition-all duration-300 w-full sm:w-auto" data-testid="hero-explore-products">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
            <div className="relative flex items-center justify-center space-x-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl px-6 py-4 md:px-8 md:py-4 min-h-[52px] transform group-hover:scale-105 transition-all shadow-xl hover:shadow-2xl">
              <span>Explore Products</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
          <Link to="/contact" className="group relative rounded-xl font-semibold transition-all duration-300 w-full sm:w-auto" data-testid="hero-contact-us">
            <div className="absolute inset-0 bg-gray-400 rounded-xl blur-lg opacity-15 group-hover:opacity-25 transition-opacity" />
            <div className="relative flex items-center justify-center bg-white/90 backdrop-blur-xl border-2 border-gray-300 text-gray-700 rounded-xl px-6 py-4 md:px-8 md:py-4 min-h-[52px] hover:bg-white hover:border-cyan-500 hover:text-cyan-700 transform group-hover:scale-105 transition-all shadow-xl hover:shadow-2xl">
              Contact Us
            </div>
          </Link>
        </div>

        {/* Stats */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-8 animate-fade-in-up"
          style={{ animationDelay: '0.4s' }}
        >
          {stats.map((stat) => (
            <div key={stat.label} className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-4 md:p-8 hover:border-cyan-300 transition-all duration-500 transform group-hover:-translate-y-2 group-hover:shadow-2xl h-[130px] md:h-[180px] flex flex-col items-center justify-center">
                <div className="w-full h-10 md:h-16 flex items-center justify-center mb-2 md:mb-4">
                  <div className="text-2xl sm:text-3xl md:text-5xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent leading-none transform group-hover:scale-110 transition-transform duration-300">
                    {stat.isStatic ? stat.value : <CountUp value={stat.value} duration={2000} />}
                  </div>
                </div>
                <div className="w-full h-10 md:h-12 flex items-center justify-center">
                  <div className="text-gray-600 text-[11px] sm:text-xs md:text-sm font-medium text-center leading-tight px-1 md:px-2">
                    {stat.label}
                  </div>
                </div>
                <div className="absolute top-2 right-2 md:top-4 md:right-4 w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full blur-lg group-hover:scale-150 transition-transform duration-500" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="hidden md:block absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-cyan-500/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-cyan-500 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
